import { Button, Dialog, DialogActions, DialogContent, DialogTitle, MenuItem, Stack } from '@mui/material'
import * as React from 'react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { LabeledSelect } from '@omnea-app/frontend/components/shared/form/inputs/labeled/LabeledSelect'
import { LabeledTextField } from '@omnea-app/frontend/components/shared/form/inputs/labeled/LabeledTextField'

export interface SavedFilterSettings {
  isPublic: boolean
  name: string
}

interface SavedFilterSettingsDialogProps {
  isOpen: boolean
  onClose: () => void
  onSubmit(settings: SavedFilterSettings): void
  initialValues?: SavedFilterSettings
  displayVisibilityDropdown: boolean
}

export const SavedFilterSettingsDialog: React.FC<SavedFilterSettingsDialogProps> = ({
  isOpen,
  onClose,
  initialValues,
  onSubmit,
  displayVisibilityDropdown,
}) => {
  const { t } = useTranslation('requests')

  const [isPublic, setPublic] = useState(initialValues?.isPublic ?? false)
  const [name, setName] = useState(initialValues?.name ?? '')
  const [nameErrorMessage, setNameErrorMessage] = useState<string | undefined>(undefined)

  function validateName(): boolean {
    if (!name.trim()) {
      setNameErrorMessage(t('create-user-request-filter.dialog.name-required'))
      return false
    }
    setNameErrorMessage(undefined)
    return true
  }

  function handleSubmit() {
    const nameIsValid = validateName()
    if (!nameIsValid) return

    onSubmit({ isPublic, name })
  }

  return (
    <Dialog open={isOpen}>
      <DialogTitle>{t('create-user-request-filter.dialog.title')}</DialogTitle>
      <DialogContent sx={{ marginTop: 4 }}>
        <Stack gap={4}>
          <LabeledTextField
            label={t('create-user-request-filter.dialog.name')}
            autoFocus
            value={name}
            onChange={(e) => setName(e.target.value)}
            error={!!nameErrorMessage}
            helperText={nameErrorMessage}
            required
          />
          {displayVisibilityDropdown && (
            <LabeledSelect
              label={t('create-user-request-filter.dialog.type')}
              value={isPublic ? 'public' : 'private'}
              onChange={(e) => setPublic(e.target.value === 'public')}
              helperText={isPublic ? t('create-user-request-filter.dialog.helper') : undefined}
            >
              <MenuItem value="public">{t('create-user-request-filter.dialog.type-public')}</MenuItem>
              <MenuItem value="private">{t('create-user-request-filter.dialog.type-private')}</MenuItem>
            </LabeledSelect>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button variant="contained" color="secondary" onClick={onClose}>
          {t('create-user-request-filter.dialog.cancel')}
        </Button>
        <Button variant="contained" color="primary" onClick={handleSubmit}>
          {initialValues ? t('create-user-request-filter.dialog.update') : t('create-user-request-filter.dialog.save')}
        </Button>
      </DialogActions>
    </Dialog>
  )
}
